"use client";

import { useEffect, useState } from "react";

import type { Route } from "next";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { ROUTES } from "@unreserved/constants";
import { useNotificationsInfinite } from "@unreserved/hooks";
import { Avatar, AvatarFallback, AvatarImage } from "@unreserved/ui/components/avatar";
import { cn, propertyDetailsUtils } from "@unreserved/utils";

import { LoaderIcon } from "@/app/_components/icons";
import { HelpIcon } from "@/app/_components/icons/header/help";
import {
  AddEnquiryModal,
  EnquiryModalShell,
  type EnquiryModalShellCloseOptions,
} from "@/app/_components/my-enquiries/modal/add-enquiry-modal";
import type { AddEnquiryPickerType } from "@/app/_components/my-enquiries/modal/enquiry-types";
import { ProfileMenuDesktop } from "@/app/_components/my-profile/profile-menu";
import {
  NotificationTriggerContent,
  NotificationsMenuDesktop,
} from "@/app/_components/notifications/notifications-menu";
import { Button } from "@/app/_components/shared/button";
import { DynamicModalWrapper } from "@/app/_components/shared/dynamic-modal-wrapper";
import { IMAGES } from "@/app/_libs/constants/images";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuthStore } from "@/stores/auth-store";

const EditProfileSheet = dynamic(
  () =>
    import("@/app/_components/my-profile/edit-profile/edit-profile-sheet").then((mod) => ({
      default: mod.EditProfileSheet,
    })),
  { ssr: false },
);

const ChangePasswordSheet = dynamic(
  () =>
    import("@/app/_components/my-profile/change-password/change-password-sheet").then((mod) => ({
      default: mod.ChangePasswordSheet,
    })),
  { ssr: false },
);

const NAV_ITEMS: { label: string; href: Route }[] = [
  { label: "Dashboard", href: ROUTES.DASHBOARD.HOME },
  { label: "My Properties", href: ROUTES.DASHBOARD.MY_PROPERTIES },
  { label: "Enquiries", href: ROUTES.DASHBOARD.MY_ENQUIRIES },
  { label: "Offers", href: ROUTES.DASHBOARD.OFFERS },
  { label: "Update Listing", href: ROUTES.DASHBOARD.MANAGE_LISTING as Route },
];

// Only surface the dashboard nav on screens where these links can be shown
// active / interchanged. Unrelated pages (e.g. /videos, /contact-us) are excluded.
const NAV_VISIBLE_PREFIXES = [
  ROUTES.DASHBOARD.HOME,
  ROUTES.DASHBOARD.MY_PROPERTIES,
  ROUTES.DASHBOARD.MY_ENQUIRIES,
  ROUTES.DASHBOARD.OFFERS,
  ROUTES.DASHBOARD.ENQUIRED_PROPERTIES,
  ROUTES.DASHBOARD.MANAGE_LISTING,
];

function isPathMatch(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function Header({ isLoggedIn = true }: { isLoggedIn?: boolean }) {
  const isMobile = useIsMobile();
  const pathname = usePathname() ?? "";
  const showNav = NAV_VISIBLE_PREFIXES.some((prefix) => isPathMatch(pathname, prefix));
  const [, setProfileOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [changePassOpen, setChangePassOpen] = useState(false);
  const profile = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthResolved = useAuthStore((state) => state.isAuthResolved);
  // combined these so profile hides on logout //
  const effectiveLoggedIn = isLoggedIn && isAuthenticated;
  const showAuthLoader = isLoggedIn && !isAuthResolved;
  const { data: notificationsData } = useNotificationsInfinite({
    enabled: effectiveLoggedIn,
  });
  const unreadCount = notificationsData?.pages?.[0]?.data?.totalUnreadCount ?? 0;

  const [isEnquiryPickerOpen, setIsEnquiryPickerOpen] = useState(false);

  const [enquiryFormState, setEnquiryFormState] = useState<{
    open: boolean;
    type: AddEnquiryPickerType | null;
  }>({ open: false, type: null });

  // Get user data for avatar
  const displayName = propertyDetailsUtils.toString(profile?.fullName) || "";
  const avatarUrl = profile?.avatar ? propertyDetailsUtils.toString(profile.avatar.fileUrl) : "";

  // Reset all states when switching between mobile and desktop
  useEffect(() => {
    setProfileOpen(false);
    setEditOpen(false);
    setChangePassOpen(false);
  }, [isMobile]);

  const handleSelectEnquiryType = (type: AddEnquiryPickerType) => {
    setIsEnquiryPickerOpen(false);
    setEnquiryFormState({ open: true, type });
  };

  const handleEnquiryFormOpenChange = (open: boolean, options?: EnquiryModalShellCloseOptions) => {
    if (!open) {
      setEnquiryFormState({ open: false, type: null });
      if (options == null) {
        setIsEnquiryPickerOpen(true);
      }
    }
  };

  return (
    <header className="border-border-100 bg-background-muted-50 flex h-16 items-center justify-between border-b px-4 py-3 sm:h-20 sm:px-6 sm:py-4 md:h-[92px] md:px-[42px] md:py-[24px]">
      <Link href={ROUTES.HOME} className="flex cursor-pointer items-center">
        <div className="relative h-[28px] w-[98px] shrink-0 sm:h-[36px] sm:w-[126px] md:h-[44px] md:w-[154px]">
          <Image
            src={IMAGES.LOGO}
            alt="Unreserved Logo"
            fill
            className="h-full w-full"
            sizes="154px"
            priority
            objectFit="contain"
          />
        </div>
      </Link>

      {effectiveLoggedIn && showNav && (
        <nav className="hidden lg:flex lg:items-center lg:gap-8">
          {NAV_ITEMS.map((item) => {
            const active = isPathMatch(pathname, item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "text-sm transition-colors",
                  active
                    ? "text-typography-900 font-semibold"
                    : "text-typography-500 hover:text-typography-900 font-medium",
                )}>
                {item.label}
              </Link>
            );
          })}
        </nav>
      )}

      {effectiveLoggedIn && (
        <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
          {isMobile !== false && (
            <div className="md:hidden">
              <Link
                href={ROUTES.NOTIFICATIONS}
                className={cn(
                  "bg-background-50 relative flex size-10 cursor-pointer items-center justify-center rounded-full p-2 md:size-12 md:p-2",
                )}
                aria-label="Notifications">
                <NotificationTriggerContent count={unreadCount} />
              </Link>
            </div>
          )}
          {isMobile === false && (
            <div className="hidden md:block">
              <NotificationsMenuDesktop count={unreadCount} />
            </div>
          )}

          {/* Help / Enquiry Button — opens the Add Enquiry modal flow */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsEnquiryPickerOpen(true)}
            className={cn(
              "!bg-background-50 hover:!bg-background-50 dark:!bg-background-50 dark:hover:!bg-background-50 flex size-10 cursor-pointer items-center justify-center rounded-full p-2 hover:!text-inherit sm:size-11 md:size-12 md:p-2",
            )}
            aria-label="Add Enquiry">
            <div className="relative flex size-7 items-center justify-center sm:size-7 md:size-8">
              <HelpIcon className="h-full w-full" />
            </div>
          </Button>

          {/* Divider */}
          <div className="bg-border-200 h-10 w-0.5 sm:h-11 md:h-12" aria-hidden="true" />

          {/* User Profile - Completely separate for Mobile and Desktop */}
          {/* Mobile: Navigate to Profile Page */}

          {isMobile !== false && (
            <div className="md:hidden">
              <Link
                href={ROUTES.PROFILE}
                className={cn(
                  "flex size-10 cursor-pointer items-center justify-center rounded-full",
                )}
                aria-label="User Profile">
                <Avatar className="size-10 rounded-full">
                  <AvatarImage src={avatarUrl} alt={displayName} />
                  <AvatarFallback className="bg-primary-100 text-typography-900 text-sm font-medium">
                    {displayName.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Link>
              {/* Sheets for Edit, Change Password, and Logout - Only render when mobile */}
              {editOpen && isMobile && (
                <EditProfileSheet open={editOpen} onOpenChange={setEditOpen} />
              )}
              {changePassOpen && isMobile && (
                <ChangePasswordSheet open={changePassOpen} onOpenChange={setChangePassOpen} />
              )}
            </div>
          )}

          {/* Desktop: Popover */}
          {isMobile === false && (
            <div className="hidden md:block">
              <ProfileMenuDesktop />
            </div>
          )}
        </div>
      )}
      {showAuthLoader && (
        <div
          className="flex items-center justify-center"
          aria-label="Loading account"
          aria-busy="true">
          <LoaderIcon className="text-primary-700 h-6 w-6 animate-spin" />
        </div>
      )}
      {!effectiveLoggedIn && !showAuthLoader && (
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="h-9 border-black px-6 text-black hover:bg-black hover:text-white"
            asChild>
            <Link href={ROUTES.AUTH.LOGIN}>Login</Link>
          </Button>
          <Button className="bg-primary-700 hover:bg-primary-700/90 h-9 px-6 text-white" asChild>
            <Link href={ROUTES.AUTH.SIGNUP}>Sign Up</Link>
          </Button>
        </div>
      )}
      <DynamicModalWrapper
        open={isEnquiryPickerOpen}
        onOpenChange={setIsEnquiryPickerOpen}
        title="Add Enquiry"
        dismissible={true}
        closeOnOverlayClick={true}>
        <AddEnquiryModal onSelectEnquiryType={handleSelectEnquiryType} />
      </DynamicModalWrapper>

      <EnquiryModalShell
        open={enquiryFormState.open}
        onOpenChange={handleEnquiryFormOpenChange}
        selectedType={enquiryFormState.type}
      />
    </header>
  );
}
